import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Table,
  Button,
  Input,
  Space,
  Tag,
  Popconfirm,
  message,
  Card,
  Row,
  Col,
  Select,
  Empty,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
  FlagOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { listFeatureFlags, deleteFeatureFlag } from '@/api/features'
import type { FeatureFlag } from '@/types/feature'
import { useAuth } from '@/hooks/useAuth'
import { usePageHeader } from '@/hooks/usePageHeader'

const { Search } = Input

export const FeatureList = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState('')
  const [scopeFilter, setScopeFilter] = useState<'user' | 'facility' | undefined>(undefined)
  const [typeFilter, setTypeFilter] = useState<'application' | 'config' | undefined>(undefined)

  // Check if user is ADMIN
  const isAdmin = user?.role === 'ADMIN'

  // Fetch feature flags with React Query
  const { data, isLoading } = useQuery({
    queryKey: ['features', page, pageSize, search, scopeFilter, typeFilter],
    queryFn: () =>
      listFeatureFlags({
        page,
        limit: pageSize,
        search: search || undefined,
        scope: scopeFilter,
        flag_type: typeFilter,
      }),
  })

  // Delete feature flag mutation
  const deleteMutation = useMutation({
    mutationFn: deleteFeatureFlag,
    onSuccess: () => {
      message.success('Feature flag deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['features'] })
    },
    onError: () => {
      message.error('Failed to delete feature flag')
    },
  })

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id)
  }

  // Handle create navigation
  const handleCreate = useCallback(() => {
    navigate('/features/create')
  }, [navigate])

  // Memoize page header config
  const pageHeaderConfig = useMemo(() => {
    return {
      title: <h1 style={{ margin: 0 }}>Feature Flags</h1>,
      extra: isAdmin ? (
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Create Feature Flag
        </Button>
      ) : undefined,
    }
  }, [isAdmin, handleCreate])

  // Set page header in main AppLayout header
  usePageHeader(pageHeaderConfig)

  const columns: ColumnsType<FeatureFlag> = [
    {
      title: 'Feature Key',
      dataIndex: 'feature_key',
      key: 'feature_key',
      sorter: (a, b) => a.feature_key.localeCompare(b.feature_key),
      render: (key: string) => (
        <Space>
          <FlagOutlined />
          <code>{key}</code>
        </Space>
      ),
    },
    {
      title: 'Label',
      dataIndex: 'label',
      key: 'label',
      sorter: (a, b) => a.label.localeCompare(b.label),
    },
    {
      title: 'Scope',
      dataIndex: 'scope',
      key: 'scope',
      render: (scope: string) => (
        <Tag color={scope === 'user' ? 'blue' : 'green'}>
          {scope === 'user' ? 'User' : 'Facility'}
        </Tag>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'flag_type',
      key: 'flag_type',
      render: (type: string | null) => (
        <Tag color={type === 'application' ? 'purple' : 'orange'}>
          {type === 'application' ? 'Application' : 'Config'}
        </Tag>
      ),
    },
    {
      title: 'Default Value',
      dataIndex: 'default_value',
      key: 'default_value',
      render: (value: boolean) => (
        <Tag color={value ? 'success' : 'default'}>{value ? 'Enabled' : 'Disabled'}</Tag>
      ),
    },
    {
      title: 'Targets',
      dataIndex: 'targetCount',
      key: 'targetCount',
      render: (count: number = 0) => (
        <Tag color="cyan">
          {count} target{count !== 1 ? 's' : ''}
        </Tag>
      ),
      sorter: (a, b) => (a.targetCount || 0) - (b.targetCount || 0),
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (date: string) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/features/${record.id}`)}
          >
            View
          </Button>
          {isAdmin && (
            <>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => navigate(`/features/${record.id}/edit`)}
              >
                Edit
              </Button>
              <Popconfirm
                title="Delete feature flag"
                description="Are you sure you want to delete this feature flag? This will remove all target configurations."
                onConfirm={() => handleDelete(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <Button type="link" danger icon={<DeleteOutlined />}>
                  Delete
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div>
      <Card>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={16}>
            <Col flex="auto">
              <Search
                placeholder="Search by feature key or label"
                allowClear
                enterButton={<SearchOutlined />}
                size="large"
                onSearch={setSearch}
              />
            </Col>
            <Col>
              <Select
                placeholder="Filter by scope"
                allowClear
                style={{ width: 150 }}
                size="large"
                onChange={setScopeFilter}
                value={scopeFilter}
              >
                <Select.Option value="user">User</Select.Option>
                <Select.Option value="facility">Facility</Select.Option>
              </Select>
            </Col>
            <Col>
              <Select
                placeholder="Filter by type"
                allowClear
                style={{ width: 150 }}
                size="large"
                onChange={setTypeFilter}
                value={typeFilter}
              >
                <Select.Option value="application">Application</Select.Option>
                <Select.Option value="config">Config</Select.Option>
              </Select>
            </Col>
          </Row>

          <Table
            columns={columns}
            dataSource={data?.data || []}
            rowKey="id"
            loading={isLoading}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: data?.meta?.pagination.total || 0,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} feature flag${total !== 1 ? 's' : ''}`,
              onChange: (newPage, newPageSize) => {
                setPage(newPage)
                setPageSize(newPageSize)
              },
            }}
            scroll={{ x: 1400 }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    search || scopeFilter || typeFilter ? (
                      <>
                        <p>No feature flags match your search criteria</p>
                        <p style={{ color: '#888' }}>Try adjusting your filters</p>
                      </>
                    ) : (
                      <>
                        <p>No feature flags have been created yet</p>
                        {isAdmin && (
                          <p style={{ color: '#888' }}>
                            Click the "Create Feature Flag" button to get started
                          </p>
                        )}
                      </>
                    )
                  }
                >
                  {isAdmin && !search && !scopeFilter && !typeFilter && (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => navigate('/features/create')}
                    >
                      Create Your First Feature Flag
                    </Button>
                  )}
                </Empty>
              ),
            }}
          />
        </Space>
      </Card>
    </div>
  )
}
